import type { Ingredient } from "@/types"

// Canonical display order for grocery categories.
// Categories not in this list sort to the end alphabetically.
export const DEFAULT_CATEGORY_ORDER = [
  'Produce',
  'Bakery',
  'Seafood',
  'Meat',
  'Breakfast Items',
  'Dry Goods',
  'Canned Goods',
  'Condiments',
  'Spices',
  'Baking Supplies',
  'Snacks',
  'Beverages',
  'Dairy',
  'Personal Care',
  'Household',
  'Frozen Foods',
  'Other',
] as const

// Function to group ingredients by their category field
export function groupIngredientsByCategory(ingredients: Ingredient[]): Record<string, Ingredient[]> {
  const grouped: Record<string, Ingredient[]> = {}

  ingredients.forEach(ingredient => {
    const category = ingredient.category || "Uncategorized"

    if (!grouped[category]) {
      grouped[category] = []
    }

    grouped[category].push(ingredient)
  })

  return grouped
}

// Sort categories by display order (with fallback for unknown categories)
// and sort ingredients within each category alphabetically by name.
export function sortIngredientsByCategoryAndName(
  groupedIngredients: Record<string, Ingredient[]>,
  categoryOrder: readonly string[] = DEFAULT_CATEGORY_ORDER,
): Record<string, Ingredient[]> {
  const sorted: Record<string, Ingredient[]> = {}
  const orderIndex = new Map<string, number>()
  categoryOrder.forEach((cat, i) => orderIndex.set(cat, i))

  const categories = Object.keys(groupedIngredients).sort((a, b) => {
    const aIdx = orderIndex.get(a)
    const bIdx = orderIndex.get(b)

    // Known categories sort by their position in the order list
    if (aIdx !== undefined && bIdx !== undefined) return aIdx - bIdx
    // Known before unknown
    if (aIdx !== undefined) return -1
    if (bIdx !== undefined) return 1
    // Unknown categories sort alphabetically at the end
    return a.localeCompare(b)
  })

  categories.forEach(category => {
    sorted[category] = groupedIngredients[category].sort((a, b) => {
      return a.name.localeCompare(b.name)
    })
  })

  return sorted
}